/* =========================================================================
   ClueVine — How-to-play guide
   A scannable overview of every registered mode. It reads straight from the
   registry, so a newly-added mode appears here automatically. Each card is a
   short pitch (what it is, its difficulty ladder) with two actions: "How to
   play" opens the same focused modal used in-game (the full numbered steps),
   and "Play" jumps straight into today's daily.
   ========================================================================= */

import { useState } from 'react';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { allModes } from '../engine/registry';
import type { AnyPuzzleMode } from '../engine/types';
import { useGame } from '../state/GameContext';
import { useNav } from '../state/NavContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { accentVars } from '../utils/accent';
import { countWord } from '../utils/format';
import { HowToPlayModal } from './HowToPlayModal';

export function HowToScreen() {
  const { isModeEnabled } = useSiteConfig();
  const modes = allModes().filter((m) => isModeEnabled(m.id));
  const game = useGame();
  const { navigate } = useNav();
  const [selected, setSelected] = useState<AnyPuzzleMode | null>(null);

  const count = countWord(modes.length);
  const Count = count.charAt(0).toUpperCase() + count.slice(1);

  const playDaily = (mode: AnyPuzzleMode) => {
    const outcome = game.start({ modeId: mode.id, playMode: 'daily' });
    // Today's daily already done? Offer a fresh endless puzzle instead.
    if (outcome === 'done') game.start({ modeId: mode.id, playMode: 'endless' });
    navigate('play');
  };

  return (
    <div className="cv-howto">
      <div className="cv-page-head">
        <span className="cv-eyebrow">Field guide</span>
        <h1>How to play</h1>
      </div>

      <p className="cv-howto__lead">
        {Count} ways to play, one shared rhythm. Every mode has a <strong>Daily</strong> that everyone
        gets the same of, plus an endless stream — and a daily win in any mode grows your garden
        streak. Tap{' '}
        <strong>How to play</strong> on any mode for the full rules, or jump straight into today&rsquo;s
        puzzle.
      </p>

      <div className="cv-guide">
        {modes.map((mode, i) => (
          <section
            key={mode.id}
            className="cv-card cv-guide-card"
            style={accentVars(mode.accent)}
            aria-labelledby={`guide-${mode.id}`}
          >
            <header className="cv-mode-card__top">
              <span className="cv-mode-card__glyph">
                <Icon name={mode.glyph} size={26} />
              </span>
              <div>
                <div className="cv-guide-card__heading">
                  <span className="cv-guide-card__num" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span id={`guide-${mode.id}`} className="cv-mode-card__name">
                    {mode.name}
                  </span>
                </div>
                <div className="cv-mode-card__tag">{mode.tagline}</div>
              </div>
            </header>

            <p className="cv-mode-card__desc">{mode.description}</p>

            <div className="cv-guide-card__levels">
              <span className="cv-faint">Levels</span>
              {mode.difficulties.map((d) => (
                <span key={d.id} className="cv-chip" title={d.blurb}>
                  {d.label}
                </span>
              ))}
            </div>

            <div className="cv-guide-card__foot">
              <Button size="sm" variant="ghost" iconLeft="bulb" onClick={() => setSelected(mode)}>
                How to play
              </Button>
              <Button size="sm" iconLeft="play" onClick={() => playDaily(mode)}>
                Play
              </Button>
            </div>
          </section>
        ))}
      </div>

      {selected ? (
        <HowToPlayModal
          mode={selected}
          open
          onClose={() => setSelected(null)}
          onPlay={() => {
            setSelected(null);
            playDaily(selected);
          }}
        />
      ) : null}
    </div>
  );
}
